import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import type { BulkCatalogAction, BulkCatalogResponse, BulkCatalogResult } from '../../api';
import { extractApiErrorMessage } from '../../utils/apiError';
import type { useCatalogSelection } from './useCatalogSelection';

interface UseCatalogBulkOptions<T> {
  /** Singular noun for confirmation copy, e.g. "product" */
  entityLabel: string;
  /** Plural noun; defaults to `${entityLabel}s` */
  entityLabelPlural?: string;
  /** API call for this catalog's bulk endpoint */
  run: (action: BulkCatalogAction, ids: string[]) => Promise<BulkCatalogResponse>;
  getId: (item: T) => string;
  /** Immutable update of an item's active flag */
  withActive: (item: T, isActive: boolean) => T;
  setItems: Dispatch<SetStateAction<T[]>>;
  selection: ReturnType<typeof useCatalogSelection>;
  onError: (message: string | null) => void;
}

/**
 * Confirm → call bulk endpoint → apply the result to local state.
 *
 * After a `delete`, any items the server kept because complaints reference
 * them stay selected, so the natural next click is "Deactivate" on exactly
 * those rows.
 */
export function useCatalogBulk<T>({
  entityLabel,
  entityLabelPlural = `${entityLabel}s`,
  run,
  getId,
  withActive,
  setItems,
  selection,
  onError,
}: UseCatalogBulkOptions<T>) {
  const [busyAction, setBusyAction] = useState<BulkCatalogAction | null>(null);
  const [result, setResult] = useState<BulkCatalogResult | null>(null);

  const runAction = useCallback(
    async (action: BulkCatalogAction) => {
      const ids = selection.selectedIds;
      if (ids.length === 0 || busyAction) return;

      const n = ids.length;
      const noun = n === 1 ? entityLabel : entityLabelPlural;
      const message =
        action === 'delete'
          ? `Permanently delete ${n} ${noun}?\n\nAny ${entityLabel} referenced by a complaint is kept automatically (deactivate those instead). This cannot be undone.`
          : action === 'deactivate'
            ? `Deactivate ${n} ${noun}?\n\nThey will be hidden from the complaint form. Existing complaints are unaffected and you can reactivate them later.`
            : `Reactivate ${n} ${noun}? They will appear in the complaint form again.`;
      if (!window.confirm(message)) return;

      setBusyAction(action);
      onError(null);
      setResult(null);
      try {
        const response = await run(action, ids);
        if (!response.status || !response.data) {
          onError(`Bulk ${action} failed`);
          return;
        }
        const data = response.data;
        const processed = new Set(data.processed);

        setItems(prev => {
          if (action === 'delete') {
            // Also drop rows the server says no longer exist
            const gone = new Set([
              ...processed,
              ...data.skipped.filter(s => s.reason === 'not_found').map(s => s.id),
            ]);
            return prev.filter(item => !gone.has(getId(item)));
          }
          const active = action === 'activate';
          return prev.map(item => (processed.has(getId(item)) ? withActive(item, active) : item));
        });

        if (action === 'delete') {
          const keptInUse = data.skipped.filter(s => s.reason === 'in_use').map(s => s.id);
          selection.clear();
          if (keptInUse.length > 0) selection.toggleAll(keptInUse);
        } else {
          selection.clear();
        }
        setResult(data);
      } catch (err) {
        onError(extractApiErrorMessage(err, `Bulk ${action} failed`));
      } finally {
        setBusyAction(null);
      }
    },
    [
      busyAction,
      entityLabel,
      entityLabelPlural,
      getId,
      onError,
      run,
      selection,
      setItems,
      withActive,
    ]
  );

  const dismissResult = useCallback(() => setResult(null), []);

  return { busyAction, result, runAction, dismissResult };
}
